/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Dialog, LoadingSection, Typography2 } from '@coursera/cds-core';

import _t from 'i18n!nls/enterprise-admin-messaging-v2';

type Props = {
  /**
   * Duration (ms) the first caption shows before flipping to the second.
   * Purely cosmetic — the transition to Review is driven by the generate
   * request resolving in the orchestrator, not by this timer. Exposed so
   * tests can pin it short.
   */
  stageDurationMs?: number;
};

const DEFAULT_STAGE_DURATION_MS = 1500;

const styles = {
  body: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--cds-spacing-200);
    min-height: 240px;
    padding: var(--cds-spacing-400) 0;
  `,
};

/**
 * Two-stage loading state shown between Define-goal and Review-and-send.
 * Sequentially flashes "Defining the right audience…" then "Crafting the
 * perfect message…" before calling `onComplete`. Stand-in for two future
 * sequential API calls (audience resolution + message generation).
 */
const GeneratingScreen = ({ stageDurationMs = DEFAULT_STAGE_DURATION_MS }: Props) => {
  const [stage, setStage] = React.useState<'audience' | 'message'>('audience');

  React.useEffect(() => {
    // Toggle the caption back and forth between "Defining the right audience…"
    // and "Crafting the perfect message…" for the whole generate period.
    // Cosmetic only — the orchestrator moves to Review when the generate
    // request resolves (or times out).
    const timer = window.setInterval(
      () => setStage((current) => (current === 'audience' ? 'message' : 'audience')),
      stageDurationMs
    );
    return () => window.clearInterval(timer);
  }, [stageDurationMs]);

  const caption = stage === 'audience' ? _t('Defining the right audience…') : _t('Crafting the perfect message…');

  return (
    <Dialog.Content>
      <div css={styles.body} role="status" aria-busy="true" data-testid="ai-message-modal-generating">
        <LoadingSection role="presentation" />
        <Typography2 component="p" variant="bodyPrimary">
          {caption}
        </Typography2>
      </div>
    </Dialog.Content>
  );
};

export default GeneratingScreen;
